import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  getWorkbooks, 
  createWorkbook, 
  updateWorkbook,
  deleteWorkbook,
  getLearners,
  createLearner,
  getDocuments,
  uploadDocument,
  deleteDocument,
  getQuestions,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  getLearnerProgress,
  deleteLearner,
  overrideSubmission,
  getSubmissionTrace,
  getNeedsAttention,
  getAdminLearnerQuestionSubmissions,
  getCourseworkQueue,
  getCourseworkReview,
  returnCoursework,
  approveCoursework,
  revokeCoursework,
  saveCourseworkFeedback,
  downloadEvidencePack
} from '../lib/api';
import formatApiError from '../lib/formatApiError';
import formatTimestamp from '../lib/formatTimestamp';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Books, 
  SignOut, 
  Plus, 
  Trash, 
  Users, 
  FileText,
  Upload,
  Eye,
  Download,
  CheckCircle,
  Warning,
  Clock,
  Circle,
  GearSix,
  ChartBar,
  PencilSimple,
  CaretDown,
  CaretUp,
  ClipboardText,
  ArrowCounterClockwise
} from '@phosphor-icons/react';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('workbooks');
  const [workbooks, setWorkbooks] = useState([]);
  const [learners, setLearners] = useState([]);
  const [needsAttention, setNeedsAttention] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workbookLoadError, setWorkbookLoadError] = useState('');
  
  // Workbook form
  const [newWorkbook, setNewWorkbook] = useState({ title: '', description: '' });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [workbookEditDialogOpen, setWorkbookEditDialogOpen] = useState(false);
  const [editingWorkbook, setEditingWorkbook] = useState(null);
  const [editingWorkbookTitle, setEditingWorkbookTitle] = useState('');
  const [editingWorkbookIncluded, setEditingWorkbookIncluded] = useState(true);
  
  // Question form
  const [selectedWorkbook, setSelectedWorkbook] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState({
    text: '', spec_ref: '', command_word: 'describe', guidance: '', marking_criteria: '', reference_material: '', plagiarism_exempt: false, name_aliases: '', coverage_groups: '', min_length: 50, min_bullets: 3, min_valid_points: 1, order: 0
  });
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  
  // Edit question
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  
  // Document upload
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [docType, setDocType] = useState('textbook');
  const [uploading, setUploading] = useState(false);
  
  // Learner progress
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [learnerProgress, setLearnerProgress] = useState([]);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);

  // Per-question attempt history in the Progress dialog (PR 7): fetched from
  // the admin attempt-history endpoint on first expand, keyed by question_id.
  const [attemptHistories, setAttemptHistories] = useState({});
  const [expandedAttempts, setExpandedAttempts] = useState({});

  // Needs-attention tab (PR 7): which entries show their full latest answer.
  const [expandedAnswers, setExpandedAnswers] = useState({});

  // Coursework tab (PR 8a): the sign-off queue and the review dialog.
  const [courseworkQueue, setCourseworkQueue] = useState([]);
  const [courseworkDialogOpen, setCourseworkDialogOpen] = useState(false);
  const [courseworkReview, setCourseworkReview] = useState(null);
  const [courseworkLearner, setCourseworkLearner] = useState(null);
  // PR 8a.1: which review-dialog question row has its override actions open.
  const [courseworkOverrideFor, setCourseworkOverrideFor] = useState(null);
  // PR 8b: assessor feedback draft for the review dialog (prefilled on open;
  // deliberately NOT reset by reloadCoursework so an unsaved draft survives
  // an override/approve/return action mid-edit).
  const [assessorFeedback, setAssessorFeedback] = useState('');
  const [savingAssessorFeedback, setSavingAssessorFeedback] = useState(false);
  const [assessorFeedbackSaved, setAssessorFeedbackSaved] = useState(false);
  const [downloadingEvidencePack, setDownloadingEvidencePack] = useState(false);

  // Submission trace viewer
  const [traceDialogOpen, setTraceDialogOpen] = useState(false);
  const [traceData, setTraceData] = useState(null);
  const [traceQuestionText, setTraceQuestionText] = useState('');

  // Learner form
  const [learnerDialogOpen, setLearnerDialogOpen] = useState(false);
  const [newLearner, setNewLearner] = useState({ name: '', email: '', password: '' });
  const [learnerCreateError, setLearnerCreateError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const getWorkbookLoadErrorMessage = (error) => {
    if (error.response?.status === 401) {
      return 'Your admin session could not be verified. Please sign in again, then refresh this dashboard.';
    }
    if (error.response?.status === 403) {
      return 'Your account does not have permission to view admin workbooks.';
    }
    return 'Workbooks could not be loaded. Please refresh the page or try again later.';
  };

  const loadData = async () => {
    setLoading(true);
    setWorkbookLoadError('');

    try {
      const wbRes = await getWorkbooks();
      setWorkbooks(wbRes.data);
    } catch (error) {
      console.error('Failed to load workbooks:', error);
      setWorkbooks([]);
      setSelectedWorkbook(null);
      setWorkbookLoadError(getWorkbookLoadErrorMessage(error));
    }

    try {
      const learnersRes = await getLearners();
      setLearners(learnersRes.data);
    } catch (error) {
      console.error('Failed to load learners:', error);
      setLearners([]);
    } finally {
      setLoading(false);
    }

    try {
      const attentionRes = await getNeedsAttention();
      setNeedsAttention(attentionRes.data || []);
    } catch (error) {
      console.error('Failed to load needs-attention list:', error);
      setNeedsAttention([]);
    }

    try {
      const courseworkRes = await getCourseworkQueue();
      setCourseworkQueue(courseworkRes.data || []);
    } catch (error) {
      console.error('Failed to load coursework queue:', error);
      setCourseworkQueue([]);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleCreateWorkbook = async () => {
    if (!newWorkbook.title.trim()) return;
    try {
      await createWorkbook(newWorkbook);
      setNewWorkbook({ title: '', description: '' });
      setCreateDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to create workbook:', error);
    }
  };


  const handleEditWorkbook = (workbook) => {
    setEditingWorkbook(workbook);
    setEditingWorkbookTitle(workbook.title || '');
    // PR 8a: included_in_coursework defaults to true (pre-8a workbooks have no flag).
    setEditingWorkbookIncluded(workbook.included_in_coursework !== false);
    setWorkbookEditDialogOpen(true);
  };

  const handleUpdateWorkbook = async () => {
    const title = editingWorkbookTitle.trim();
    if (!editingWorkbook || !title) return;

    try {
      const response = await updateWorkbook(editingWorkbook._id, {
        title,
        included_in_coursework: editingWorkbookIncluded
      });
      const updatedWorkbook = response.data;

      setWorkbookEditDialogOpen(false);
      setEditingWorkbook(null);
      setEditingWorkbookTitle('');

      if (selectedWorkbook?._id === updatedWorkbook._id) {
        setSelectedWorkbook({ ...selectedWorkbook, ...updatedWorkbook });
      }

      loadData();
    } catch (error) {
      console.error('Failed to update workbook:', error);
    }
  };

  const handleDeleteWorkbook = async (id) => {
    if (!window.confirm('Are you sure you want to delete this workbook?')) return;
    try {
      await deleteWorkbook(id);
      loadData();
    } catch (error) {
      console.error('Failed to delete workbook:', error);
    }
  };

  const loadWorkbookDetails = async (workbook) => {
    setSelectedWorkbook(workbook);
    try {
      const [qRes, dRes] = await Promise.all([
        getQuestions(workbook._id),
        getDocuments(workbook._id)
      ]);
      setQuestions(qRes.data);
      setDocuments(dRes.data);
    } catch (error) {
      console.error('Failed to load workbook details:', error);
    }
  };

  const showGuidanceLintWarnings = (response) => {
    const warnings = response?.data?.guidance_lint_warnings;
    if (warnings && warnings.length > 0) {
      window.alert('Question saved, but check the guidance text:\n\n' + warnings.join('\n\n'));
    }
  };

  // name_aliases is edited as one equivalence group per line ("A = B = C")
  // and sent to the API as a list of those lines.
  const parseNameAliases = (text) => (text || '').split('\n').map((line) => line.trim()).filter(Boolean);

  // coverage_groups is edited as a JSON array of
  // {label, criterion_numbers, min_required} objects; empty means none.
  const parseCoverageGroups = (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return [];
    const parsed = JSON.parse(trimmed);
    if (!Array.isArray(parsed)) {
      throw new Error('Coverage groups must be a JSON array');
    }
    return parsed;
  };

  const formatCoverageGroups = (groups) =>
    groups && groups.length > 0 ? JSON.stringify(groups, null, 2) : '';

  const handleCreateQuestion = async () => {
    if (!newQuestion.text.trim() || !selectedWorkbook) return;
    let coverageGroups;
    try {
      coverageGroups = parseCoverageGroups(newQuestion.coverage_groups);
    } catch (error) {
      alert('Coverage Groups is not valid JSON: ' + error.message);
      return;
    }
    try {
      const response = await createQuestion({
        ...newQuestion,
        name_aliases: parseNameAliases(newQuestion.name_aliases),
        coverage_groups: coverageGroups,
        workbook_id: selectedWorkbook._id,
        order: questions.length
      });
      setNewQuestion({ text: '', spec_ref: '', command_word: 'describe', guidance: '', marking_criteria: '', reference_material: '', plagiarism_exempt: false, name_aliases: '', coverage_groups: '', min_length: 50, min_bullets: 3, min_valid_points: 1, order: 0 });
      setQuestionDialogOpen(false);
      loadWorkbookDetails(selectedWorkbook);
      showGuidanceLintWarnings(response);
    } catch (error) {
      console.error('Failed to create question:', error);
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await deleteQuestion(id);
      loadWorkbookDetails(selectedWorkbook);
    } catch (error) {
      console.error('Failed to delete question:', error);
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion({
      _id: question._id,
      text: question.text,
      spec_ref: question.spec_ref || '',
      command_word: question.command_word,
      guidance: question.guidance || '',
      marking_criteria: question.marking_criteria || '',
      reference_material: question.reference_material || '',
      plagiarism_exempt: !!question.plagiarism_exempt,
      name_aliases: (question.name_aliases || []).join('\n'),
      coverage_groups: formatCoverageGroups(question.coverage_groups),
      min_length: question.min_length || 50,
      min_bullets: question.min_bullets || 3,
      min_valid_points: question.min_valid_points || 1
    });
    setEditDialogOpen(true);
  };

  const handleUpdateQuestion = async () => {
    if (!editingQuestion || !editingQuestion.text.trim()) return;
    let coverageGroups;
    try {
      coverageGroups = parseCoverageGroups(editingQuestion.coverage_groups);
    } catch (error) {
      alert('Coverage Groups is not valid JSON: ' + error.message);
      return;
    }
    try {
      const response = await updateQuestion(editingQuestion._id, {
        text: editingQuestion.text,
        spec_ref: editingQuestion.spec_ref,
        command_word: editingQuestion.command_word,
        guidance: editingQuestion.guidance,
        marking_criteria: editingQuestion.marking_criteria,
        reference_material: editingQuestion.reference_material,
        plagiarism_exempt: editingQuestion.plagiarism_exempt,
        name_aliases: parseNameAliases(editingQuestion.name_aliases),
        coverage_groups: coverageGroups,
        min_length: editingQuestion.min_length,
        min_bullets: editingQuestion.min_bullets,
        min_valid_points: editingQuestion.min_valid_points
      });
      setEditDialogOpen(false);
      setEditingQuestion(null);
      loadWorkbookDetails(selectedWorkbook);
      showGuidanceLintWarnings(response);
    } catch (error) {
      console.error('Failed to update question:', error);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !selectedWorkbook) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('workbook_id', selectedWorkbook._id);
      formData.append('doc_type', docType);
      
      await uploadDocument(formData);
      setUploadFile(null);
      setUploadDialogOpen(false);
      loadWorkbookDetails(selectedWorkbook);
    } catch (error) {
      console.error('Failed to upload document:', error);
      alert('Failed to upload document: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await deleteDocument(id);
      loadWorkbookDetails(selectedWorkbook);
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };


  const handleCreateLearner = async () => {
    setLearnerCreateError('');

    try {
      await createLearner(newLearner);
      setNewLearner({ name: '', email: '', password: '' });
      setLearnerDialogOpen(false);
      loadData();
    } catch (error) {
      // formatApiError renders FastAPI 422 detail arrays as readable
      // "field: message" lines (fixes "Failed to create learner: [object Object]").
      const message = formatApiError(error);
      setLearnerCreateError(message);
      alert('Failed to create learner: ' + message);
    }
  };

  const loadLearnerProgress = async (learner) => {
    setSelectedLearner(learner);
    // Attempt histories belong to one learner and go stale after an
    // override — refetch on next expand.
    setAttemptHistories({});
    setExpandedAttempts({});
    try {
      const response = await getLearnerProgress(learner._id);
      setLearnerProgress(response.data);
      setProgressDialogOpen(true);
    } catch (error) {
      console.error('Failed to load progress:', error);
    }
  };

  // PR 7: "View attempts (n)" expander — collapsed by default, fetching the
  // learner's full attempt history for that question from the admin endpoint
  // on first expand. PR 8a: takes the learner id explicitly so the SAME
  // expander serves both the Progress dialog and the Coursework review
  // screen (no parallel component).
  const toggleAttemptHistory = async (learnerId, question) => {
    const questionId = question.question_id;
    const isOpen = !!expandedAttempts[questionId];
    setExpandedAttempts((prev) => ({ ...prev, [questionId]: !isOpen }));
    if (isOpen || attemptHistories[questionId] || !learnerId) return;

    setAttemptHistories((prev) => ({ ...prev, [questionId]: { loading: true, attempts: [] } }));
    try {
      const response = await getAdminLearnerQuestionSubmissions(learnerId, questionId);
      setAttemptHistories((prev) => ({
        ...prev,
        [questionId]: { loading: false, attempts: response.data || [] }
      }));
    } catch (error) {
      console.error('Failed to load attempt history:', error);
      setAttemptHistories((prev) => ({
        ...prev,
        [questionId]: { loading: false, attempts: [], error: formatApiError(error) }
      }));
    }
  };

  // Overridden submissions are labelled, e.g. "accepted — assessor override".
  const attemptStatusLabel = (attempt) => {
    const readable = (attempt.status || 'unknown').replace(/_/g, ' ');
    return attempt.admin_override ? `${readable} — assessor override` : readable;
  };

  // PR 7.1 (owner ruling, 10 July 2026): consecutive "duplicate" attempts
  // collapse into one summary row under the attempt they repeat. Display
  // grouping only — the fetched history stays complete. An override or a
  // changed answer breaks the run: an overridden duplicate renders as a
  // normal row, and a duplicate of a different answer starts a new run.
  const groupAttemptRows = (attempts) => {
    const rows = [];
    for (const attempt of attempts) {
      const collapsible = attempt.validation_type === 'duplicate' && !attempt.admin_override;
      const previous = rows[rows.length - 1];
      if (collapsible && previous?.type === 'duplicate_run' && previous.answer === attempt.answer) {
        previous.attempts.push(attempt);
      } else if (collapsible) {
        rows.push({ type: 'duplicate_run', answer: attempt.answer, attempts: [attempt] });
      } else {
        rows.push({ type: 'attempt', attempt });
      }
    }
    return rows;
  };

  // PR 7 attempt-history expander, shared JSX (PR 8a reuse): rendered inside
  // BOTH the Progress dialog and the Coursework review dialog. `q` needs
  // {question_id, question_text, attempts}; rows come from groupAttemptRows.
  const renderAttemptExpander = (learnerId, q) => {
    const history = attemptHistories[q.question_id];
    const attemptsOpen = !!expandedAttempts[q.question_id];
    return (
      <div className="mt-1">
        <button
          type="button"
          onClick={() => toggleAttemptHistory(learnerId, q)}
          className="inline-flex items-center gap-1 text-xs font-medium text-[#3B5A7A] hover:underline"
          data-testid={`view-attempts-${q.question_id}`}
        >
          {attemptsOpen ? <CaretUp size={12} /> : <CaretDown size={12} />}
          View attempts ({q.attempts})
        </button>
        {attemptsOpen && (
          <div className="mt-2 space-y-2" data-testid={`attempt-history-${q.question_id}`}>
            {history?.loading && (
              <p className="text-xs text-slate-400">Loading attempts...</p>
            )}
            {history?.error && (
              <p className="text-xs text-red-600">Failed to load attempts: {history.error}</p>
            )}
            {history && !history.loading && !history.error && history.attempts.length === 0 && (
              <p className="text-xs text-slate-400">No attempts recorded.</p>
            )}
            {groupAttemptRows(history?.attempts || []).map((row) => {
              if (row.type === 'duplicate_run') {
                // One summary row per run of unchanged resubmissions — the
                // original row above carries the answer, feedback and Trace.
                const first = row.attempts[0];
                const last = row.attempts[row.attempts.length - 1];
                return (
                  <div
                    key={`duplicate-run-${first.submission_id}`}
                    className="p-2 bg-slate-50 border border-slate-200 rounded"
                    data-testid={`duplicate-run-${first.submission_id}`}
                  >
                    <p className="text-xs text-slate-500">
                      Resubmitted unchanged {row.attempts.length} times (attempts {first.attempt_number}–{last.attempt_number}, last {formatTimestamp(last.created_at)})
                    </p>
                  </div>
                );
              }
              const attempt = row.attempt;
              return (
                <div key={attempt.submission_id} className="p-2 bg-white border border-slate-200 rounded" data-testid={`attempt-${attempt.submission_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-slate-600">
                      Attempt {attempt.attempt_number}
                      {attempt.created_at && (
                        <span className="text-slate-400 font-normal"> · {formatTimestamp(attempt.created_at)}</span>
                      )}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium ${
                        attempt.status === 'needs_improvement' ? 'text-amber-600' : 'text-emerald-600'
                      }`}>
                        {attemptStatusLabel(attempt)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewTrace({ submission_id: attempt.submission_id, question_text: q.question_text })}
                        className="h-6 px-2 text-slate-500 hover:text-[#3B5A7A] hover:bg-slate-100"
                        title="View the marking trace for this attempt"
                        data-testid={`attempt-trace-${attempt.submission_id}`}
                      >
                        <Eye size={12} className="mr-1" />
                        Trace
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-700 mt-1 whitespace-pre-wrap">{attempt.answer}</p>
                  {attempt.status === 'needs_improvement' && attempt.feedback && (
                    <p className="text-xs text-slate-500 mt-1 italic">Feedback: {attempt.feedback}</p>
                  )}
                  {attempt.admin_override && attempt.admin_feedback && (
                    <p className="text-xs text-slate-500 mt-1">
                      <span className="font-medium">
                        {attempt.status === 'needs_improvement'
                          ? 'Note shown to the learner'
                          : 'Internal note — not shown to the learner'}
                        :
                      </span>{' '}
                      "{attempt.admin_feedback}"
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // --- Coursework sign-off (PR 8a) -----------------------------------------

  const reloadCoursework = async (userId) => {
    try {
      const queueRes = await getCourseworkQueue();
      setCourseworkQueue(queueRes.data || []);
    } catch (error) {
      console.error('Failed to reload coursework queue:', error);
    }
    if (userId) {
      try {
        const reviewRes = await getCourseworkReview(userId);
        setCourseworkReview(reviewRes.data);
      } catch (error) {
        console.error('Failed to reload coursework review:', error);
      }
    }
  };

  const openCourseworkReview = async (row) => {
    setCourseworkLearner(row);
    // The attempt-history cache belongs to one learner (same rule as the
    // Progress dialog) — reset on open so histories can't go stale.
    setAttemptHistories({});
    setExpandedAttempts({});
    setCourseworkOverrideFor(null);
    try {
      const response = await getCourseworkReview(row.user_id);
      setCourseworkReview(response.data);
      setAssessorFeedback(response.data.signoff?.assessor_feedback || '');
      setAssessorFeedbackSaved(false);
      setCourseworkDialogOpen(true);
    } catch (error) {
      console.error('Failed to load coursework review:', error);
      alert('Failed to load coursework review: ' + formatApiError(error));
    }
  };

  // PR 8b: word-capped assessor feedback for the evidence pack. The backend
  // enforces the 250-word limit; the counter here is advisory.
  const assessorFeedbackWordCount = assessorFeedback.trim()
    ? assessorFeedback.trim().split(/\s+/).length
    : 0;

  const handleSaveAssessorFeedback = async () => {
    if (!courseworkLearner) return;
    setSavingAssessorFeedback(true);
    try {
      await saveCourseworkFeedback(courseworkLearner.user_id, assessorFeedback);
      setAssessorFeedbackSaved(true);
    } catch (error) {
      console.error('Failed to save assessor feedback:', error);
      alert('Failed to save assessor feedback: ' + formatApiError(error));
    } finally {
      setSavingAssessorFeedback(false);
    }
  };

  // PR 8b: authenticated blob download. With responseType 'blob' an error
  // body arrives as a Blob, so the 409/403 detail is decoded before display.
  const handleDownloadEvidencePack = async () => {
    if (!courseworkLearner) return;
    setDownloadingEvidencePack(true);
    try {
      const response = await downloadEvidencePack(courseworkLearner.user_id);
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const slug = (courseworkLearner.learner_name || 'learner')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'learner';
      const fallbackName = `coursework-evidence-${slug}-${new Date().toISOString().slice(0, 10)}.docx`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = match ? match[1] : fallbackName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download evidence pack:', error);
      let message = formatApiError(error);
      const blobBody = error.response?.data;
      if (blobBody instanceof Blob) {
        try {
          message = JSON.parse(await blobBody.text())?.detail || message;
        } catch (parseError) {
          // fall through to the generic message
        }
      }
      alert('Failed to download evidence pack: ' + message);
    } finally {
      setDownloadingEvidencePack(false);
    }
  };

  const handleApproveCoursework = async () => {
    if (!courseworkLearner) return;
    // The confirm dialog shows the assessor declaration (owner-editable
    // backend constant). PR 8b.1: several assessors share this login, so the
    // approver types their name — REQUIRED, stored on the signoff and in the
    // approved event's note; approval is blocked until it is non-empty.
    const declaration = courseworkReview?.assessor_declaration_text || '';
    const assessorName = window.prompt(
      `Approve ${courseworkLearner.learner_name}'s coursework?\n\n${declaration}\n\nSeveral assessors share this login — type YOUR NAME (required) to confirm this declaration and approve:`,
      ''
    );
    if (assessorName === null) return; // cancelled
    if (!assessorName.trim()) {
      alert('Your name is required to approve — the approval must record who made it.');
      return;
    }
    try {
      await approveCoursework(courseworkLearner.user_id, assessorName.trim());
      await reloadCoursework(courseworkLearner.user_id);
    } catch (error) {
      console.error('Failed to approve coursework:', error);
      alert('Failed to approve coursework: ' + formatApiError(error));
    }
  };

  const handleReturnCoursework = async () => {
    if (!courseworkLearner) return;
    // PR 8b.1: the return is attributed to the typed name in its audit event.
    const assessorName = window.prompt(
      `Return ${courseworkLearner.learner_name}'s coursework?\n\nSeveral assessors share this login — type YOUR NAME (required); the return is recorded against it:`,
      ''
    );
    if (assessorName === null) return; // cancelled
    if (!assessorName.trim()) {
      alert('Your name is required to return coursework — the return must record who made it.');
      return;
    }
    const note = window.prompt(
      `A return needs at least one answer already overridden to needs improvement — that override's learner-visible note is the learner's instruction.\n\nOptional note for the audit record:`,
      ''
    );
    if (note === null) return; // cancelled
    try {
      await returnCoursework(courseworkLearner.user_id, note.trim() || null, assessorName.trim());
      await reloadCoursework(courseworkLearner.user_id);
    } catch (error) {
      console.error('Failed to return coursework:', error);
      alert('Failed to return coursework: ' + formatApiError(error));
    }
  };

  const handleRevokeCoursework = async () => {
    if (!courseworkLearner) return;
    // PR 8b.1: the revoke is attributed to the typed name in its audit event.
    const assessorName = window.prompt(
      `Revoke the approval of ${courseworkLearner.learner_name}'s coursework?\n\nSeveral assessors share this login — type YOUR NAME (required); the revoke is recorded against it:`,
      ''
    );
    if (assessorName === null) return; // cancelled
    if (!assessorName.trim()) {
      alert('Your name is required to revoke an approval — the revoke must record who made it.');
      return;
    }
    const reason = window.prompt(
      `Reason (required — recorded permanently in the audit history):`,
      ''
    );
    if (reason === null) return; // cancelled
    if (!reason.trim()) {
      alert('A reason is required to revoke an approval.');
      return;
    }
    try {
      await revokeCoursework(courseworkLearner.user_id, reason.trim(), assessorName.trim());
      await reloadCoursework(courseworkLearner.user_id);
    } catch (error) {
      console.error('Failed to revoke approval:', error);
      alert('Failed to revoke approval: ' + formatApiError(error));
    }
  };

  const courseworkStatusChip = (status) => {
    const styles = {
      submitted: 'bg-blue-50 text-blue-700',
      returned: 'bg-amber-50 text-amber-700',
      approved: 'bg-emerald-50 text-emerald-700'
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-50 text-slate-500'}`}>
        {status}
      </span>
    );
  };

  const submittedCourseworkCount = courseworkQueue.filter((row) => row.status === 'submitted').length;

  // PR 7: "Open progress" from a Needs-attention entry reuses the existing
  // Progress dialog loader; the learner record is looked up from state (with
  // the entry's own fields as fallback so the dialog still opens).
  const openProgressFromEntry = (entry) => {
    const learner = learners.find((candidate) => candidate._id === entry.user_id) || {
      _id: entry.user_id,
      name: entry.learner_name,
      email: entry.learner_email
    };
    loadLearnerProgress(learner);
  };

  // Shared override flow (PR 8a.1 — extracted, exactly as renderAttemptExpander
  // was shared): the SAME prompt wording, reject-note requirement and API call
  // for both the Progress dialog and the Coursework review dialog. Targets the
  // LATEST submission (question.submission_id). Returns true when an override
  // was applied so each caller refreshes its own view.
  // Owner ruling 4 (10 July 2026): the accept-note is optional and internal
  // only (admin/audit evidence — never shown to the learner); the reject
  // note is required and learner-visible.
  const promptAndOverrideSubmission = async (question, status) => {
    const isAccept = status === 'accepted';
    const label = isAccept ? 'accept' : 'mark as needs improvement';
    const noteLabel = isAccept
      ? 'Internal note — not shown to the learner (optional):'
      : 'Feedback for the learner (required — shown to the learner verbatim):';
    const feedback = window.prompt(
      `Override: ${label} the latest answer for "${question.question_text}".\n\n${noteLabel}`,
      ''
    );
    if (feedback === null) return false; // cancelled
    if (!isAccept && !feedback.trim()) {
      alert('A learner-visible note is required when overriding to needs improvement.');
      return false;
    }
    try {
      await overrideSubmission(question.submission_id, {
        status,
        feedback: feedback.trim() || null
      });
      return true;
    } catch (error) {
      console.error('Failed to override submission:', error);
      alert('Failed to override submission: ' + (error.response?.data?.detail || error.message));
      return false;
    }
  };

  const handleOverrideSubmission = async (question, status) => {
    const applied = await promptAndOverrideSubmission(question, status);
    if (applied && selectedLearner) {
      await loadLearnerProgress(selectedLearner);
    }
  };

  // PR 8a.1: the same flow from a coursework review row — on success the
  // review data is re-fetched so the row's badge/chip, the digest and the
  // Approve/Return preconditions update in place (dialog stays open). The
  // attempt-history cache is reset like the Progress-dialog loader does.
  const handleCourseworkOverride = async (question, status) => {
    const applied = await promptAndOverrideSubmission(question, status);
    if (applied) {
      setCourseworkOverrideFor(null);
      setAttemptHistories({});
      setExpandedAttempts({});
      if (courseworkLearner) {
        await reloadCoursework(courseworkLearner.user_id);
      }
    }
  };

  const handleViewTrace = async (question) => {
    try {
      const response = await getSubmissionTrace(question.submission_id);
      setTraceData(response.data);
      setTraceQuestionText(question.question_text);
      setTraceDialogOpen(true);
    } catch (error) {
      console.error('Failed to load trace:', error);
      alert('Failed to load trace: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteLearner = async (learner) => {
    const confirmed = window.confirm(
      `Delete learner ${learner.name} (${learner.email})? This will remove their account, submissions, and final reviews.`
    );

    if (!confirmed) return;

    try {
      await deleteLearner(learner._id);

      if (selectedLearner?._id === learner._id) {
        setProgressDialogOpen(false);
        setSelectedLearner(null);
        setLearnerProgress([]);
      }

      loadData();
    } catch (error) {
      console.error('Failed to delete learner:', error);
      alert('Failed to delete learner: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      accepted: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50' },
      needs_improvement: { icon: Warning, color: 'text-amber-500', bg: 'bg-amber-50' },
      draft: { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-50' },
      not_started: { icon: Circle, color: 'text-slate-300', bg: 'bg-slate-50' }
    };
    const badge = badges[status] || badges.not_started;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${badge.bg}`}>
        <Icon size={12} weight="fill" className={badge.color} />
        <span className={badge.color}>{status.replace('_', ' ')}</span>
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="app-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#3B5A7A] flex items-center justify-center">
                <GearSix size={24} weight="duotone" className="text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900 font-['Manrope']">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">{user?.name} (Admin)</span>
              <Button variant="ghost" onClick={handleLogout} data-testid="admin-logout-btn">
                <SignOut size={20} className="mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="workbooks" className="flex items-center gap-2">
              <Books size={18} />
              Workbooks
            </TabsTrigger>
            <TabsTrigger value="learners" className="flex items-center gap-2">
              <Users size={18} />
              Learners
            </TabsTrigger>
            <TabsTrigger value="needs-attention" className="flex items-center gap-2" data-testid="needs-attention-tab">
              <Warning size={18} />
              Needs attention
              {needsAttention.length > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-500 text-white text-xs font-semibold"
                  data-testid="needs-attention-badge"
                >
                  {needsAttention.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="coursework" className="flex items-center gap-2" data-testid="coursework-tab">
              <ClipboardText size={18} />
              Coursework
              {submittedCourseworkCount > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[#3B5A7A] text-white text-xs font-semibold"
                  data-testid="coursework-badge"
                >
                  {submittedCourseworkCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Workbooks Tab */}
          <TabsContent value="workbooks">
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Workbook List */}
              <div className="lg:col-span-1">
                <div className="card p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold text-slate-900">Workbooks</h2>
                    <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-[#3B5A7A] hover:bg-[#2A4158]" data-testid="create-workbook-btn">
                          <Plus size={16} className="mr-1" />
                          New
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Create Workbook</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label>Title</Label>
                            <Input
                              value={newWorkbook.title}
                              onChange={(e) => setNewWorkbook({ ...newWorkbook, title: e.target.value })}
                              placeholder="Workbook title"
                              data-testid="workbook-title-input"
                            />
                          </div>
                          <div>
                            <Label>Description</Label>
                            <Textarea
                              value={newWorkbook.description}
                              onChange={(e) => setNewWorkbook({ ...newWorkbook, description: e.target.value })}
                              placeholder="Workbook description"
                              data-testid="workbook-description-input"
                            />
                          </div>
                          <Button onClick={handleCreateWorkbook} className="w-full bg-[#3B5A7A] hover:bg-[#2A4158]" data-testid="save-workbook-btn">
                            Create Workbook
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                  <Dialog open={workbookEditDialogOpen} onOpenChange={setWorkbookEditDialogOpen}>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Edit Workbook</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div>
                          <Label htmlFor="edit-workbook-title">Workbook title</Label>
                          <Input
                            id="edit-workbook-title"
                            value={editingWorkbookTitle}
                            onChange={(e) => setEditingWorkbookTitle(e.target.value)}
                            placeholder="Workbook title"
                            data-testid="edit-workbook-title-input"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="edit-workbook-included"
                            checked={editingWorkbookIncluded}
                            onCheckedChange={(checked) => setEditingWorkbookIncluded(checked === true)}
                            data-testid="edit-workbook-included-checkbox"
                          />
                          <Label htmlFor="edit-workbook-included" className="font-normal">
                            Included in coursework (untick for sandbox/test workbooks so they never block
                            learners' coursework submission)
                          </Label>
                        </div>
                        <Button
                          onClick={handleUpdateWorkbook}
                          disabled={!editingWorkbookTitle.trim()}
                          className="w-full bg-[#3B5A7A] hover:bg-[#2A4158]"
                          data-testid="save-workbook-title-btn"
                        >
                          Save Workbook
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  <div className="space-y-2">
                    {workbooks.map((wb) => (
                      <div
                        key={wb._id}
                        className={`p-3 rounded-md cursor-pointer transition-colors ${
                          selectedWorkbook?._id === wb._id ? 'bg-[#3B5A7A] text-white' : 'bg-slate-50 hover:bg-slate-100'
                        }`}
                        onClick={() => loadWorkbookDetails(wb)}
                        data-testid={`workbook-item-${wb._id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium truncate">{wb.title}</span>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleEditWorkbook(wb); }}
                              className={selectedWorkbook?._id === wb._id ? 'text-white hover:text-blue-100' : 'text-slate-400 hover:text-[#3B5A7A]'}
                              data-testid={`edit-workbook-${wb._id}`}
                            >
                              <PencilSimple size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleDeleteWorkbook(wb._id); }}
                              className={selectedWorkbook?._id === wb._id ? 'text-white hover:text-red-200' : 'text-slate-400 hover:text-red-500'}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </div>
                        <p className={`text-xs mt-1 truncate ${selectedWorkbook?._id === wb._id ? 'text-white/70' : 'text-slate-500'}`}>
                          {wb.total_questions || 0} questions
                        </p>
                      </div>
                    ))}
                    {workbookLoadError ? (
                      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="workbook-load-error">
                        <div className="flex items-start gap-2">
                          <Warning size={18} weight="fill" className="mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium">Unable to load workbooks</p>
                            <p className="mt-1">{workbookLoadError}</p>
                            {workbookLoadError.includes('sign in') && (
                              <Button
                                type="button"
                                size="sm"
                                className="mt-3 bg-[#3B5A7A] hover:bg-[#2A4158]"
                                onClick={() => navigate('/login')}
                                data-testid="workbook-auth-error-login-btn"
                              >
                                Go to login
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : loading ? (
                      <p className="text-slate-400 text-sm text-center py-4">Loading workbooks...</p>
                    ) : workbooks.length === 0 && (
                      <p className="text-slate-400 text-sm text-center py-4">No workbooks yet</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Workbook Details */}
              <div className="lg:col-span-2">
                {selectedWorkbook ? (
                  <div className="space-y-6">
                    {/* Questions */}
                    <div className="card p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-slate-900">Questions</h3>
                        <Dialog open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
                          <DialogTrigger asChild>
                            <Button size="sm" className="bg-[#3B5A7A] hover:bg-[#2A4158]" data-testid="add-question-btn">
                              <Plus size={16} className="mr-1" />
                              Add Question
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-lg">
                            <DialogHeader>
                              <DialogTitle>Add Question</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div>
                                <Label>Question Text</Label>
                                <Textarea
                                  value={newQuestion.text}
                                  onChange={(e) => setNewQuestion({ ...newQuestion, text: e.target.value })}
                                  placeholder="Enter the question"
                                  data-testid="question-text-input"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label>Command Word</Label>
                                  <Select value={newQuestion.command_word} onValueChange={(v) => setNewQuestion({ ...newQuestion, command_word: v })}>
                                    <SelectTrigger data-testid="command-word-select">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="state">State</SelectItem>
                                      <SelectItem value="identify">Identify</SelectItem>
                                      <SelectItem value="describe">Describe</SelectItem>
                                      <SelectItem value="explain">Explain</SelectItem>
                                      <SelectItem value="outline">Outline</SelectItem>
                                      <SelectItem value="list">List</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>Spec Reference</Label>
                                  <Input
                                    value={newQuestion.spec_ref}
                                    onChange={(e) => setNewQuestion({ ...newQuestion, spec_ref: e.target.value })}
                                    placeholder="e.g., 1.2.3"
                                    data-testid="spec-ref-input"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label>Guidance Notes (optional)</Label>
                                <Textarea
                                  value={newQuestion.guidance}
                                  onChange={(e) => setNewQuestion({ ...newQuestion, guidance: e.target.value })}
                                  placeholder="Additional guidance for the learner"
                                  data-testid="guidance-input"
                                />
                              </div>
                              <div>
                                <Label>Marking Criteria (internal — not shown to learners)</Label>
                                <Textarea
                                  value={newQuestion.marking_criteria}
                                  onChange={(e) => setNewQuestion({ ...newQuestion, marking_criteria: e.target.value })}
                                  placeholder="Internal pass/fail criteria for AI marking"
                                  data-testid="marking-criteria-input"
                                />
                              </div>
                              <div>
                                <Label>Reference Material (internal — replaces document retrieval for this question)</Label>
                                <Textarea
                                  value={newQuestion.reference_material}
                                  onChange={(e) => setNewQuestion({ ...newQuestion, reference_material: e.target.value })}
                                  placeholder="Paste the exact learning-material passage for this question"
                                  data-testid="reference-material-input"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id="plagiarism-exempt"
                                  checked={newQuestion.plagiarism_exempt}
                                  onCheckedChange={(checked) => setNewQuestion({ ...newQuestion, plagiarism_exempt: checked === true })}
                                  data-testid="plagiarism-exempt-checkbox"
                                />
                                <Label htmlFor="plagiarism-exempt" className="font-normal">
                                  Plagiarism exempt (official-name list questions)
                                </Label>
                              </div>
                              {newQuestion.command_word === 'list' && (
                                <div>
                                  <Label>Name Aliases (closed-name lists — one group per line, equivalent spellings separated by =)</Label>
                                  <Textarea
                                    value={newQuestion.name_aliases}
                                    onChange={(e) => setNewQuestion({ ...newQuestion, name_aliases: e.target.value })}
                                    placeholder={'Health and Safety at Work Act = HASAWA = HSWA\nThe Green Guide = Guide to Safety at Sports Grounds'}
                                    data-testid="name-aliases-input"
                                  />
                                </div>
                              )}
                              <div>
                                <Label>Coverage Groups (internal — JSON list; each group needs min_required satisfied criteria from its numbers)</Label>
                                <Textarea
                                  value={newQuestion.coverage_groups}
                                  onChange={(e) => setNewQuestion({ ...newQuestion, coverage_groups: e.target.value })}
                                  placeholder={'[{"label": "why reports matter", "criterion_numbers": [1, 2, 3], "min_required": 1}]'}
                                  data-testid="coverage-groups-input"
                                />
                              </div>
                              <div>
                                <Label>{newQuestion.command_word === 'list' ? 'Minimum Bullet Points' : 'Minimum Word Count'}</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  value={newQuestion.command_word === 'list' ? newQuestion.min_bullets : newQuestion.min_length}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value) || (newQuestion.command_word === 'list' ? 1 : 50);
                                    setNewQuestion(newQuestion.command_word === 'list'
                                      ? { ...newQuestion, min_bullets: value }
                                      : { ...newQuestion, min_length: value }
                                    );
                                  }}
                                  data-testid={newQuestion.command_word === 'list' ? 'min-bullets-input' : 'min-length-input'}
                                />
                              </div>
                              {newQuestion.command_word !== 'list' && (
                                <div>
                                  <Label>Minimum Valid Points (pass count — how many satisfied criteria categories are needed)</Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={newQuestion.min_valid_points}
                                    onChange={(e) => setNewQuestion({ ...newQuestion, min_valid_points: parseInt(e.target.value) || 1 })}
                                    data-testid="min-valid-points-input"
                                  />
                                </div>
                              )}
                              <Button onClick={handleCreateQuestion} className="w-full bg-[#3B5A7A] hover:bg-[#2A4158]" data-testid="save-question-btn">
                                Add Question
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                      
                      <div className="space-y-3">
                        {questions.map((q, index) => (
                          <div key={q._id} className="p-4 bg-slate-50 rounded-md" data-testid={`question-item-${q._id}`}>
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium text-slate-400">Q{index + 1}</span>
                                  <span className="command-word-badge">{q.command_word}</span>
                                  {q.spec_ref && (
                                    <span className="text-xs text-slate-400 mono">Ref: {q.spec_ref}</span>
                                  )}
                                </div>
                                <p className="text-sm text-slate-700">{q.text}</p>
                                {q.guidance && (
                                  <p className="text-xs text-slate-500 mt-1 italic">Guidance: {q.guidance}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleEditQuestion(q)} className="text-slate-400 hover:text-[#3B5A7A]" data-testid={`edit-question-${q._id}`}>
                                  <PencilSimple size={16} />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteQuestion(q._id)} className="text-slate-400 hover:text-red-500">
                                  <Trash size={16} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                        {questions.length === 0 && (
                          <p className="text-slate-400 text-sm text-center py-4">No questions yet</p>
                        )}
                      </div>
                    </div>

                    {/* Edit Question Dialog */}
                    <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle>Edit Question</DialogTitle>
                        </DialogHeader>
                        {editingQuestion && (
                          <div className="space-y-4 py-4">
                            <div>
                              <Label>Question Text</Label>
                              <Textarea
                                value={editingQuestion.text}
                                onChange={(e) => setEditingQuestion({ ...editingQuestion, text: e.target.value })}
                                placeholder="Enter the question"
                                data-testid="edit-question-text-input"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Command Word</Label>
                                <Select value={editingQuestion.command_word} onValueChange={(v) => setEditingQuestion({ ...editingQuestion, command_word: v })}>
                                  <SelectTrigger data-testid="edit-command-word-select">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="state">State</SelectItem>
                                    <SelectItem value="identify">Identify</SelectItem>
                                    <SelectItem value="describe">Describe</SelectItem>
                                    <SelectItem value="explain">Explain</SelectItem>
                                    <SelectItem value="outline">Outline</SelectItem>
                                    <SelectItem value="list">List</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label>Spec Reference</Label>
                                <Input
                                  value={editingQuestion.spec_ref}
                                  onChange={(e) => setEditingQuestion({ ...editingQuestion, spec_ref: e.target.value })}
                                  placeholder="e.g., 1.2.3"
                                  data-testid="edit-spec-ref-input"
                                />
                              </div>
                            </div>
                            <div>
                              <Label>Guidance Notes (optional)</Label>
                              <Textarea
                                value={editingQuestion.guidance}
                                onChange={(e) => setEditingQuestion({ ...editingQuestion, guidance: e.target.value })}
                                placeholder="Additional guidance for the learner"
                                data-testid="edit-guidance-input"
                              />
                            </div>
                            <div>
                              <Label>Marking Criteria (internal — not shown to learners)</Label>
                              <Textarea
                                value={editingQuestion.marking_criteria}
                                onChange={(e) => setEditingQuestion({ ...editingQuestion, marking_criteria: e.target.value })}
                                placeholder="Internal pass/fail criteria for AI marking"
                                data-testid="edit-marking-criteria-input"
                              />
                            </div>
                            <div>
                              <Label>Reference Material (internal — replaces document retrieval for this question)</Label>
                              <Textarea
                                value={editingQuestion.reference_material}
                                onChange={(e) => setEditingQuestion({ ...editingQuestion, reference_material: e.target.value })}
                                placeholder="Paste the exact learning-material passage for this question"
                                data-testid="edit-reference-material-input"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id="edit-plagiarism-exempt"
                                checked={editingQuestion.plagiarism_exempt}
                                onCheckedChange={(checked) => setEditingQuestion({ ...editingQuestion, plagiarism_exempt: checked === true })}
                                data-testid="edit-plagiarism-exempt-checkbox"
                              />
                              <Label htmlFor="edit-plagiarism-exempt" className="font-normal">
                                Plagiarism exempt (official-name list questions)
                              </Label>
                            </div>
                            {editingQuestion.command_word === 'list' && (
                              <div>
                                <Label>Name Aliases (closed-name lists — one group per line, equivalent spellings separated by =)</Label>
                                <Textarea
                                  value={editingQuestion.name_aliases}
                                  onChange={(e) => setEditingQuestion({ ...editingQuestion, name_aliases: e.target.value })}
                                  placeholder={'Health and Safety at Work Act = HASAWA = HSWA\nThe Green Guide = Guide to Safety at Sports Grounds'}
                                  data-testid="edit-name-aliases-input"
                                />
                              </div>
                            )}
                            <div>
                              <Label>Coverage Groups (internal — JSON list; each group needs min_required satisfied criteria from its numbers)</Label>
                              <Textarea
                                value={editingQuestion.coverage_groups}
                                onChange={(e) => setEditingQuestion({ ...editingQuestion, coverage_groups: e.target.value })}
                                placeholder={'[{"label": "why reports matter", "criterion_numbers": [1, 2, 3], "min_required": 1}]'}
                                data-testid="edit-coverage-groups-input"
                              />
                            </div>
                            <div>
                              <Label>{editingQuestion.command_word === 'list' ? 'Minimum Bullet Points' : 'Minimum Word Count'}</Label>
                              <Input
                                type="number"
                                min="1"
                                value={editingQuestion.command_word === 'list' ? editingQuestion.min_bullets : editingQuestion.min_length}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || (editingQuestion.command_word === 'list' ? 1 : 50);
                                  setEditingQuestion(editingQuestion.command_word === 'list'
                                    ? { ...editingQuestion, min_bullets: value }
                                    : { ...editingQuestion, min_length: value }
                                  );
                                }}
                                data-testid={editingQuestion.command_word === 'list' ? 'edit-min-bullets-input' : 'edit-min-length-input'}
                              />
                            </div>
                            {editingQuestion.command_word !== 'list' && (
                              <div>
                                <Label>Minimum Valid Points (pass count — how many satisfied criteria categories are needed)</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  value={editingQuestion.min_valid_points}
                                  onChange={(e) => setEditingQuestion({ ...editingQuestion, min_valid_points: parseInt(e.target.value) || 1 })}
                                  data-testid="edit-min-valid-points-input"
                                />
                              </div>
                            )}
                            <Button onClick={handleUpdateQuestion} className="w-full bg-[#3B5A7A] hover:bg-[#2A4158]" data-testid="update-question-btn">
                              Update Question
                            </Button>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>

                    {/* Documents */}
                    <div className="card p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-slate-900">Learning Materials</h3>
                        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" data-testid="upload-document-btn">
                              <Upload size={16} className="mr-1" />
                              Upload
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Upload Document</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div>
                                <Label>Document Type</Label>
                                <Select value={docType} onValueChange={setDocType}>
                                  <SelectTrigger data-testid="doc-type-select">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="textbook">Textbook / Learning Guide</SelectItem>
                                    <SelectItem value="specification">Qualification Specification</SelectItem>
                                    <SelectItem value="guidance">Internal Guidance</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label>PDF or TXT File</Label>
                                <Input
                                  type="file"
                                  accept=".pdf,.txt"
                                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                  data-testid="file-input"
                                />
                              </div>
                              <Button 
                                onClick={handleUploadDocument} 
                                disabled={!uploadFile || uploading}
                                className="w-full bg-[#3B5A7A] hover:bg-[#2A4158]"
                                data-testid="upload-submit-btn"
                              >
                                {uploading ? 'Uploading...' : 'Upload Document'}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                      
                      <div className="space-y-2">
                        {documents.map((doc) => (
                          <div key={doc._id} className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                            <div className="flex items-center gap-3">
                              <FileText size={20} className="text-[#3B5A7A]" />
                              <div>
                                <p className="text-sm font-medium text-slate-700">{doc.filename}</p>
                                <p className="text-xs text-slate-400 capitalize">{doc.type}</p>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteDocument(doc._id)} className="text-slate-400 hover:text-red-500">
                              <Trash size={16} />
                            </Button>
                          </div>
                        ))}
                        {documents.length === 0 && (
                          <p className="text-slate-400 text-sm text-center py-4">No documents uploaded</p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="card p-12 text-center">
                    <Books size={48} className="text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Select a workbook to manage</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Learners Tab */}
          <TabsContent value="learners">
            <div className="card">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-4">
                <h2 className="font-semibold text-slate-900">Registered Learners</h2>
                <Dialog open={learnerDialogOpen} onOpenChange={setLearnerDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-[#3B5A7A] hover:bg-[#2A4158]" data-testid="create-learner-btn">
                      <Plus size={16} className="mr-1" />
                      Add Learner
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Learner</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label htmlFor="learner-name">Full name</Label>
                        <Input
                          id="learner-name"
                          value={newLearner.name}
                          onChange={(e) => setNewLearner({ ...newLearner, name: e.target.value })}
                          placeholder="Enter learner name"
                          data-testid="learner-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="learner-email">Email</Label>
                        <Input
                          id="learner-email"
                          type="email"
                          value={newLearner.email}
                          onChange={(e) => setNewLearner({ ...newLearner, email: e.target.value })}
                          placeholder="Enter learner email"
                          data-testid="learner-email-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="learner-password">Temporary password</Label>
                        <Input
                          id="learner-password"
                          type="password"
                          value={newLearner.password}
                          onChange={(e) => setNewLearner({ ...newLearner, password: e.target.value })}
                          placeholder="Enter temporary password"
                          data-testid="learner-password-input"
                        />
                      </div>
                      {learnerCreateError && (
                        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="learner-create-error">
                          {learnerCreateError}
                        </div>
                      )}
                      <Button onClick={handleCreateLearner} className="w-full bg-[#3B5A7A] hover:bg-[#2A4158]" data-testid="save-learner-btn">
                        Create Learner
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="divide-y divide-slate-100">
                {learners.map((learner) => (
                  <div key={learner._id} className="p-4 flex items-center justify-between" data-testid={`learner-item-${learner._id}`}>
                    <div>
                      <p className="font-medium text-slate-900">{learner.name}</p>
                      <p className="text-sm text-slate-500">{learner.email}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-slate-600">{learner.accepted_submissions || 0} accepted</p>
                        <p className="text-xs text-slate-400">{learner.total_submissions || 0} total submissions</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => loadLearnerProgress(learner)} data-testid={`view-progress-${learner._id}`}>
                          <Eye size={16} className="mr-1" />
                          Progress
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteLearner(learner)}
                          className="text-red-600 hover:text-red-700 hover:border-red-300"
                          data-testid={`delete-learner-${learner._id}`}
                        >
                          <Trash size={16} className="mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {learners.length === 0 && (
                  <p className="text-slate-400 text-sm text-center py-8">No learners registered yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Needs Attention Tab — learners flagged after consecutive failed
              attempts on one question (review_flag; threshold 10 per owner
              ruling, 10 July 2026). */}
          <TabsContent value="needs-attention">
            <div className="card">
              <div className="p-4 border-b border-slate-200">
                <h2 className="font-semibold text-slate-900">Needs attention</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Questions where a learner has had 10 or more failed attempts in a row. Their latest answer is shown below each entry — view the marking trace, or open their Progress dialog to see every attempt and override the result.
                </p>
              </div>
              <div className="divide-y divide-slate-100">
                {needsAttention.map((entry) => {
                  const entryKey = `${entry.user_id}-${entry.question_id}`;
                  const answerText = entry.last_answer || '';
                  const isLongAnswer = answerText.length > 280;
                  const answerExpanded = !!expandedAnswers[entryKey];
                  return (
                  <div key={entryKey} className="p-4" data-testid={`needs-attention-${entry.user_id}-${entry.question_id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Warning size={16} weight="fill" className="text-amber-500 flex-shrink-0" />
                          {entry.spec_ref && (
                            <span className="text-xs text-slate-400 mono">Ref: {entry.spec_ref}</span>
                          )}
                          <span className="text-xs text-slate-400">{entry.attempts} attempts</span>
                        </div>
                        <p className="text-sm font-medium text-slate-800">{entry.question_text}</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {entry.learner_name} {entry.learner_email && <span className="text-slate-400">({entry.learner_email})</span>}
                        </p>
                        {answerText && (
                          <div className="mt-2 p-3 bg-slate-50 rounded-md" data-testid={`needs-attention-answer-${entry.user_id}-${entry.question_id}`}>
                            <p className="text-xs font-medium text-slate-500 mb-1">Latest answer</p>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">
                              {isLongAnswer && !answerExpanded ? answerText.slice(0, 280).trimEnd() + '…' : answerText}
                            </p>
                            {isLongAnswer && (
                              <button
                                type="button"
                                onClick={() => setExpandedAnswers((prev) => ({ ...prev, [entryKey]: !answerExpanded }))}
                                className="mt-1 text-xs font-medium text-[#3B5A7A] hover:underline"
                                data-testid={`needs-attention-answer-toggle-${entry.user_id}-${entry.question_id}`}
                              >
                                {answerExpanded ? 'Hide full answer' : 'Show full answer'}
                              </button>
                            )}
                          </div>
                        )}
                        {entry.last_feedback && (
                          <p className="text-xs text-slate-500 mt-2 italic">Last feedback: {entry.last_feedback}</p>
                        )}
                        <div className="flex items-center gap-2 mt-3">
                          {entry.submission_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewTrace({ submission_id: entry.submission_id, question_text: entry.question_text })}
                              className="h-7 px-2 text-slate-600"
                              title="View the marking trace for this learner's latest submission"
                              data-testid={`needs-attention-trace-${entry.user_id}-${entry.question_id}`}
                            >
                              <Eye size={14} className="mr-1" />
                              Trace
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openProgressFromEntry(entry)}
                            className="h-7 px-2 text-slate-600"
                            title="Open this learner's Progress dialog"
                            data-testid={`needs-attention-open-progress-${entry.user_id}-${entry.question_id}`}
                          >
                            <ChartBar size={14} className="mr-1" />
                            Open progress
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
                {needsAttention.length === 0 && (
                  <p className="text-slate-400 text-sm text-center py-8">Nothing needs attention right now</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Coursework Tab (PR 8a) — the sign-off queue: one row per
              learner coursework submission. The badge counts submitted. */}
          <TabsContent value="coursework">
            <div className="card">
              <div className="p-4 border-b border-slate-200">
                <h2 className="font-semibold text-slate-900">Coursework sign-off</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Coursework submitted by learners for assessor review. Open a submission to review every unit,
                  then approve it or return it to the learner.
                </p>
              </div>
              <div className="divide-y divide-slate-100">
                {courseworkQueue.map((row) => (
                  <div key={row.user_id} className="p-4 flex items-center justify-between gap-4" data-testid={`coursework-row-${row.user_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900">{row.learner_name}</p>
                      <p className="text-sm text-slate-500">{row.learner_email}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {row.submitted_at && <>Submitted: {formatTimestamp(row.submitted_at)}</>}
                        {row.approved_at && <> · Approved: {formatTimestamp(row.approved_at)}</>}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {courseworkStatusChip(row.status)}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openCourseworkReview(row)}
                        data-testid={`coursework-review-${row.user_id}`}
                      >
                        <Eye size={16} className="mr-1" />
                        Review
                      </Button>
                    </div>
                  </div>
                ))}
                {courseworkQueue.length === 0 && (
                  <p className="text-slate-400 text-sm text-center py-8">No coursework has been submitted yet</p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Coursework Review Dialog (PR 8a C2/C3): deterministic digest at the
          top, then per unit every question with status/attempts/override/flag
          markers and the SAME attempt-history expander PR 7 built. */}
      <Dialog open={courseworkDialogOpen} onOpenChange={setCourseworkDialogOpen}>
        {/* PR 8a.2.1: DialogContent is display:grid with an implicit auto
            column, so the track sizes to the widest child's single-line
            (max-content) width — the PR 8a.2 flex-wrap chips sat in a
            containing block wider than the dialog and never wrapped.
            minmax(0,1fr) pins the column to the dialog's own width so
            children wrap instead of forcing a horizontal scrollbar. */}
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto grid-cols-[minmax(0,1fr)]">
          <DialogHeader>
            <DialogTitle>Coursework review: {courseworkLearner?.learner_name}</DialogTitle>
          </DialogHeader>
          {courseworkReview && (
            <div className="space-y-6 py-2 text-sm" data-testid="coursework-review-content">
              {/* Status + actions */}
              <div className="flex items-center justify-between gap-4 p-3 bg-slate-50 rounded-md">
                <div className="flex items-center gap-3">
                  {courseworkStatusChip(courseworkReview.signoff?.status)}
                  <div className="text-xs text-slate-500">
                    {courseworkReview.signoff?.submitted_at && (
                      <div>Submitted: {formatTimestamp(courseworkReview.signoff.submitted_at)}</div>
                    )}
                    {courseworkReview.signoff?.approved_at && (
                      <div>Approved: {formatTimestamp(courseworkReview.signoff.approved_at)}</div>
                    )}
                    {courseworkReview.signoff?.completion_date && (
                      <div>Completion date: {formatTimestamp(courseworkReview.signoff.completion_date)}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {courseworkReview.signoff?.status === 'submitted' && (
                    <>
                      <Button
                        size="sm"
                        onClick={handleApproveCoursework}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="coursework-approve-btn"
                      >
                        <CheckCircle size={16} className="mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleReturnCoursework}
                        className="text-amber-700 hover:text-amber-800 hover:border-amber-300"
                        data-testid="coursework-return-btn"
                      >
                        <Warning size={16} className="mr-1" />
                        Return to learner
                      </Button>
                    </>
                  )}
                  {courseworkReview.signoff?.status === 'approved' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRevokeCoursework}
                      className="text-red-600 hover:text-red-700 hover:border-red-300"
                      data-testid="coursework-revoke-btn"
                    >
                      <ArrowCounterClockwise size={16} className="mr-1" />
                      Revoke approval
                    </Button>
                  )}
                  {/* PR 8a.2 item 2: while returned there is no action to take
                      here (return/approve only apply while submitted) — say so
                      instead of silently showing nothing. */}
                  {courseworkReview.signoff?.status === 'returned' && (
                    <p className="text-xs font-medium text-amber-700 flex items-center gap-1" data-testid="coursework-returned-notice">
                      <Warning size={14} weight="fill" className="text-amber-500" />
                      Returned to learner — awaiting resubmission
                    </p>
                  )}
                  {/* PR 8b: the evidence pack unlocks at approval (the backend
                      409s in every other state — after a revoke it re-locks). */}
                  {courseworkReview.signoff?.status === 'approved' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleDownloadEvidencePack}
                      disabled={downloadingEvidencePack}
                      data-testid="coursework-evidence-pack-btn"
                    >
                      <Download size={16} className="mr-1" />
                      {downloadingEvidencePack ? 'Preparing…' : 'Download evidence pack'}
                    </Button>
                  ) : (
                    <div className="flex flex-col items-end">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled
                        title="Available after approval"
                        data-testid="coursework-evidence-pack-btn"
                      >
                        <Download size={16} className="mr-1" />
                        Download evidence pack
                      </Button>
                      <span className="text-[10px] text-slate-400 mt-0.5">Available after approval</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Learner declaration on record */}
              {courseworkReview.signoff?.declaration_text && (
                <p className="text-xs text-slate-500 italic">
                  Learner declaration on record: "{courseworkReview.signoff.declaration_text}"
                </p>
              )}

              {/* Deterministic review digest (C3) — computed from stored data only, no AI. */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-2">Review digest</h4>
                {(courseworkReview.digest || []).every((unit) =>
                  !unit.override_accepts.length && !unit.accepted_with_feedback.length &&
                  !unit.high_attempts.length && !unit.ever_flagged.length && !unit.plagiarism_near_threshold.length
                ) ? (
                  <p className="text-slate-400 text-xs" data-testid="coursework-digest-empty">Nothing flagged by the digest.</p>
                ) : (
                  <div className="space-y-3" data-testid="coursework-digest">
                    {(courseworkReview.digest || []).map((unit) => {
                      const hasContent = unit.override_accepts.length || unit.accepted_with_feedback.length ||
                        unit.high_attempts.length || unit.ever_flagged.length || unit.plagiarism_near_threshold.length;
                      if (!hasContent) return null;
                      return (
                        <div key={unit.workbook_id} className="p-3 bg-slate-50 rounded-md space-y-2">
                          <p className="font-medium text-slate-800">{unit.workbook_title}</p>
                          {/* PR 8a.2 item 1: individual chips instead of one
                              joined text run. Wrapping additionally needs the
                              DialogContent grid column pinned to the dialog
                              width (PR 8a.2.1, see the DialogContent above) —
                              without it this flex-wrap sits in a max-content-
                              wide track and never wraps. */}
                          {unit.override_accepts.length > 0 && (
                            <div className="text-xs text-slate-600 break-words">
                              <span className="font-medium">Accepted by assessor override:</span>
                              <div className="mt-1 flex flex-wrap gap-1" data-testid={`digest-override-accepts-${unit.workbook_id}`}>
                                {unit.override_accepts.map((q) => (
                                  <span key={q.question_id} className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded max-w-full break-words">
                                    {q.spec_ref || q.question_text}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {unit.accepted_with_feedback.length > 0 && (
                            <div className="text-xs text-slate-600 break-words">
                              <span className="font-medium">Accepted with feedback:</span>
                              <ul className="mt-1 space-y-1">
                                {unit.accepted_with_feedback.map((q) => (
                                  <li key={q.question_id}>
                                    {q.spec_ref || q.question_text}{q.note && <span className="text-slate-500 italic"> — {q.note}</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {unit.high_attempts.length > 0 && (
                            <div className="text-xs text-slate-600 break-words">
                              <span className="font-medium">5 or more attempts:</span>{' '}
                              {unit.high_attempts.map((q) => `${q.spec_ref || q.question_text} (${q.attempts})`).join(', ')}
                            </div>
                          )}
                          {unit.ever_flagged.length > 0 && (
                            <div className="text-xs text-slate-600 break-words">
                              <span className="font-medium">Previously flagged for review:</span>{' '}
                              {unit.ever_flagged.map((q) => q.spec_ref || q.question_text).join(', ')}
                            </div>
                          )}
                          {unit.plagiarism_near_threshold.length > 0 && (
                            <div className="text-xs text-slate-600 break-words">
                              <span className="font-medium">Accepted with notable learning-material overlap:</span>{' '}
                              {unit.plagiarism_near_threshold.map((q) =>
                                `${q.spec_ref || q.question_text} (${q.longest_consecutive_words} consecutive words, ${Math.round((q.near_verbatim_ratio || 0) * 100)}% near-verbatim sentences)`
                              ).join(', ')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Per-unit question lists (C2) with the shared attempt-history expander */}
              {(courseworkReview.units || []).map((unit) => (
                <div key={unit.workbook_id} className="space-y-2">
                  <h4 className="font-semibold text-slate-900">{unit.workbook_title}</h4>
                  {unit.questions.map((q, index) => (
                    <div key={q.question_id} className="p-2 bg-slate-50 rounded text-sm" data-testid={`coursework-question-${q.question_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-600 truncate flex-1">
                          Q{index + 1}{q.spec_ref ? ` (${q.spec_ref})` : ''}: {q.question_text}...
                        </span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {/* PR 8a.2 item 2: mark the question(s) the return
                              reopened while the coursework sits returned. */}
                          {courseworkReview.signoff?.status === 'returned' &&
                            (courseworkReview.signoff?.reopened_question_ids || []).includes(q.question_id) && (
                            <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" title="Reopened by the return — awaiting the learner's resubmission" data-testid={`coursework-reopened-${q.question_id}`}>
                              reopened
                            </span>
                          )}
                          {q.accepted_by_override && (
                            <span className="text-xs font-medium text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full" title="This accept is an assessor override">
                              assessor override
                            </span>
                          )}
                          {q.ever_review_flag && (
                            <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" title="This question carried a review flag at some point">
                              was flagged
                            </span>
                          )}
                          <span className="text-xs text-slate-400">{q.attempts} attempts</span>
                          {getStatusBadge(q.status)}
                          {/* PR 8a.1: per-question override from the review
                              dialog — the SAME shared flow the Progress dialog
                              uses, targeting the latest submission. Disabled
                              while approved (the backend 409s anyway); absent
                              when there is no submission to override. */}
                          {q.submission_id && (
                            courseworkReview.signoff?.status === 'approved' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled
                                className="h-7 px-2 text-slate-300 cursor-not-allowed border-l border-slate-200 rounded-none pl-2"
                                title="Approved and locked — revoke approval first"
                                data-testid={`coursework-override-${q.question_id}`}
                              >
                                <PencilSimple size={14} className="mr-1" />
                                Override…
                              </Button>
                            ) : courseworkOverrideFor === q.question_id ? (
                              <div className="flex items-center gap-1 border-l border-slate-200 pl-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCourseworkOverride(q, 'accepted')}
                                  className="h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                  title="Override: accept this answer"
                                  data-testid={`coursework-override-accept-${q.question_id}`}
                                >
                                  <CheckCircle size={14} className="mr-1" />
                                  Accept
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCourseworkOverride(q, 'needs_improvement')}
                                  className="h-7 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  title="Override: mark this answer as needs improvement"
                                  data-testid={`coursework-override-reject-${q.question_id}`}
                                >
                                  <Warning size={14} className="mr-1" />
                                  Reject
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setCourseworkOverrideFor(null)}
                                  className="h-7 px-2 text-slate-400 hover:text-slate-600"
                                  data-testid={`coursework-override-cancel-${q.question_id}`}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setCourseworkOverrideFor(q.question_id)}
                                className="h-7 px-2 text-slate-500 hover:text-[#3B5A7A] hover:bg-slate-100 border-l border-slate-200 rounded-none pl-2"
                                title="Override the latest answer for this question"
                                data-testid={`coursework-override-${q.question_id}`}
                              >
                                <PencilSimple size={14} className="mr-1" />
                                Override…
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                      {q.submission_id && renderAttemptExpander(courseworkLearner?.user_id, q)}
                    </div>
                  ))}
                  {unit.questions.length === 0 && (
                    <p className="text-slate-400 text-xs">No questions in this unit.</p>
                  )}
                </div>
              ))}

              {/* PR 8b: assessor feedback for the evidence pack — editable in
                  every state, capped at 250 words server-side, never shown to
                  the learner (no learner endpoint returns it). */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-1">Assessor feedback</h4>
                <p className="text-xs text-slate-500 mb-2">
                  Printed in the evidence pack (max 250 words). Assessor/EQA-facing only — never shown to the learner.
                </p>
                <Textarea
                  value={assessorFeedback}
                  onChange={(event) => {
                    setAssessorFeedback(event.target.value);
                    setAssessorFeedbackSaved(false);
                  }}
                  rows={4}
                  placeholder="Overall assessor feedback for the evidence pack…"
                  data-testid="coursework-assessor-feedback"
                />
                <div className="flex items-center justify-between mt-1">
                  <span
                    className={`text-xs ${assessorFeedbackWordCount > 250 ? 'text-red-600 font-medium' : 'text-slate-400'}`}
                    data-testid="coursework-assessor-feedback-count"
                  >
                    {assessorFeedbackWordCount}/250 words
                  </span>
                  <div className="flex items-center gap-2">
                    {assessorFeedbackSaved && <span className="text-xs text-emerald-600">Saved</span>}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleSaveAssessorFeedback}
                      disabled={savingAssessorFeedback}
                      data-testid="coursework-assessor-feedback-save"
                    >
                      {savingAssessorFeedback ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Append-only audit history */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-1">History</h4>
                <div className="space-y-1">
                  {(courseworkReview.signoff?.events || []).map((event, index) => (
                    <p key={index} className="text-xs text-slate-500" data-testid={`coursework-event-${index}`}>
                      <span className="font-medium capitalize">{event.type}</span>
                      {event.at && <> · {formatTimestamp(event.at)}</>}
                      {event.note && <span className="italic"> — "{event.note}"</span>}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Learner Progress Dialog */}
      <Dialog open={progressDialogOpen} onOpenChange={setProgressDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Progress: {selectedLearner?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {learnerProgress.map((wp) => (
              <div key={wp.workbook_id} className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-slate-900">{wp.workbook_title}</h4>
                  <span className="text-sm text-slate-500">{wp.completed} / {wp.total_questions}</span>
                </div>
                <div className="space-y-2">
                  {wp.questions.map((q, index) => {
                    return (
                    <div key={q.question_id} className="p-2 bg-slate-50 rounded text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-600 truncate flex-1">Q{index + 1}: {q.question_text}...</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">{q.attempts} attempts</span>
                          {getStatusBadge(q.status)}
                          {q.submission_id && (
                            <div className="flex items-center gap-1 border-l border-slate-200 pl-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOverrideSubmission(q, 'accepted')}
                                className="h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                title="Override: accept this answer"
                                data-testid={`override-accept-${q.question_id}`}
                              >
                                <CheckCircle size={14} className="mr-1" />
                                Accept
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOverrideSubmission(q, 'needs_improvement')}
                                className="h-7 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                title="Override: mark this answer as needs improvement"
                                data-testid={`override-reject-${q.question_id}`}
                              >
                                <Warning size={14} className="mr-1" />
                                Reject
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewTrace(q)}
                                className="h-7 px-2 text-slate-500 hover:text-[#3B5A7A] hover:bg-slate-100"
                                title="View the marking trace for the latest submission"
                                data-testid={`view-trace-${q.question_id}`}
                              >
                                <Eye size={14} className="mr-1" />
                                Trace
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* PR 7: per-question attempt history — the shared
                          expander (also used by the Coursework review). */}
                      {q.submission_id && renderAttemptExpander(selectedLearner?._id, q)}
                    </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {learnerProgress.length === 0 && (
              <p className="text-slate-400 text-center py-4">No progress data</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Submission Trace Dialog */}
      <Dialog open={traceDialogOpen} onOpenChange={setTraceDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Marking trace</DialogTitle>
          </DialogHeader>
          {traceData && (
            <div className="space-y-4 py-2 text-sm" data-testid="trace-dialog-content">
              <p className="text-slate-600">{traceQuestionText}...</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                <div><span className="font-semibold text-slate-700">Status:</span> {traceData.status || '—'}</div>
                <div><span className="font-semibold text-slate-700">Validation type:</span> {traceData.validation_type || '—'}</div>
                <div><span className="font-semibold text-slate-700">Attempt:</span> {traceData.attempt_number ?? '—'}</div>
                <div><span className="font-semibold text-slate-700">Submitted:</span> {formatTimestamp(traceData.created_at)}</div>
                <div><span className="font-semibold text-slate-700">Reference source:</span> {traceData.trace?.reference_source || '—'}</div>
                <div><span className="font-semibold text-slate-700">Prompt version:</span> {traceData.trace?.prompt_version || '—'}</div>
                <div><span className="font-semibold text-slate-700">Model:</span> {traceData.trace?.model || '— (no AI call)'}</div>
              </div>

              <div>
                <h4 className="font-semibold text-slate-700 mb-1">Decision</h4>
                {traceData.trace?.decision ? (
                  <div className="bg-slate-50 rounded p-3 space-y-1">
                    {traceData.trace.decision.satisfied != null && (
                      <div>Satisfied categories: {traceData.trace.decision.satisfied} / {traceData.trace.decision.required ?? '—'} required</div>
                    )}
                    {traceData.trace.decision.score != null && (
                      <div>Score: {traceData.trace.decision.score} (threshold {traceData.trace.decision.pass_threshold ?? '—'})</div>
                    )}
                    {traceData.trace.decision.type && (
                      <div>Decision type: {traceData.trace.decision.type}</div>
                    )}
                    <div>
                      Hard fails: {traceData.trace.decision.hard_fails?.length ? traceData.trace.decision.hard_fails.join(', ') : 'none'}
                    </div>
                    <div>Resulting status: {traceData.trace.decision.status || '—'}</div>
                  </div>
                ) : (
                  <p className="text-slate-400">No decision data recorded (submission predates tracing).</p>
                )}
              </div>

              {traceData.admin_override && (
                <div>
                  <h4 className="font-semibold text-slate-700 mb-1">Assessor override</h4>
                  <div className="bg-slate-50 rounded p-3 space-y-1">
                    <div>Overridden to: {traceData.status || '—'}{traceData.overridden_at ? ` (${formatTimestamp(traceData.overridden_at)})` : ''}</div>
                    <div>
                      {/* Owner ruling 4 (10 July 2026): the override-to-accepted
                          note is internal only — this admin view is where it lives. */}
                      Note{traceData.status !== 'needs_improvement' ? ' (internal — not shown to the learner)' : ' (learner-visible)'}:{' '}
                      {traceData.admin_feedback ? `"${traceData.admin_feedback}"` : '— none —'}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <h4 className="font-semibold text-slate-700 mb-1">Plagiarism check</h4>
                {traceData.trace?.plagiarism ? (
                  <div className="bg-slate-50 rounded p-3 space-y-1">
                    <div>
                      {traceData.trace.plagiarism.exempt
                        ? 'Exempt (official-name question) — not checked'
                        : traceData.trace.plagiarism.checked
                          ? (traceData.trace.plagiarism.triggered ? 'TRIGGERED — answer closely matches learning material' : 'Checked — no match')
                          : 'Not checked (no learning material stored)'}
                    </div>
                    {traceData.trace.plagiarism.checked && (
                      <div className="text-slate-500">
                        Longest matching run: {traceData.trace.plagiarism.longest_consecutive_words} words ·
                        near-verbatim sentences: {traceData.trace.plagiarism.near_verbatim_sentences}/{traceData.trace.plagiarism.assessable_sentences}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-400">No plagiarism data recorded.</p>
                )}
              </div>

              <details>
                <summary className="cursor-pointer font-semibold text-slate-700">Full trace (raw)</summary>
                <pre className="mt-2 bg-slate-900 text-slate-100 rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(traceData.trace, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
